import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useDocumentTitle } from '@shared/hooks/useDocumentTitle';
import { cn } from '@shared/lib/cn';

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Small line above the title (e.g. academy name, breadcrumb). */
  eyebrow?: ReactNode;
  className?: string;
  /** Move focus to the h1 on mount — route-change focus management (a11y doc §2). */
  focusOnMount?: boolean;
}

export function PageHeader({ title, description, actions, eyebrow, className, focusOnMount = true }: PageHeaderProps) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focusOnMount) ref.current?.focus({ preventScroll: true });
  }, [focusOnMount]);
  useDocumentTitle(ref);
  return (
    <header className={cn('mb-6 flex flex-wrap items-end justify-between gap-4', className)}>
      <div className="min-w-0">
        {eyebrow ? <div className="mb-1 text-sm text-muted">{eyebrow}</div> : null}
        <h1 ref={ref} tabIndex={-1} className="text-2xl outline-none md:text-3xl">
          {title}
        </h1>
        {description ? <p className="mt-2 max-w-reading text-md text-secondary">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export interface FormSectionProps {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** Labelled group used by long forms: heading + description beside the fields (design system §5.1). */
export function FormSection({ title, description, children, className }: FormSectionProps) {
  const id = useId();
  const headingId = `fs${id.replace(/:/g, '')}`;
  return (
    <section
      aria-labelledby={headingId}
      className={cn('grid gap-4 border-t border-border-subtle pt-6 first:border-t-0 first:pt-0 md:grid-cols-3', className)}
    >
      <div className="md:col-span-1">
        <h2 id={headingId} className="text-lg">
          {title}
        </h2>
        {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
      </div>
      <div className="flex flex-col gap-4 md:col-span-2">{children}</div>
    </section>
  );
}

export function FormActions({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-wrap items-center justify-end gap-3 pt-2', className)}>{children}</div>;
}
